import { useApolloClient, useMutation } from "@apollo/client";
import { ImagePlus, Loader2, X } from "lucide-react";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { UPLOAD_GBP_PHOTO_MUTATION } from "@/graphql/google-business";
import { uploadAsset } from "@/lib/asset/upload-asset";
import { useSalonStore } from "@/lib/store/salon";
import { cn } from "@/lib/utils";

const CATEGORIES = [
  {
    value: "ADDITIONAL",
    labelKey: "additionalLabel",
    descKey: "additionalDesc",
  },
  {
    value: "EXTERIOR",
    labelKey: "exteriorLabel",
    descKey: "exteriorDesc",
  },
  {
    value: "INTERIOR",
    labelKey: "interiorLabel",
    descKey: "interiorDesc",
  },
  {
    value: "AT_WORK",
    labelKey: "atWorkLabel",
    descKey: "atWorkDesc",
  },
  {
    value: "TEAMS",
    labelKey: "teamsLabel",
    descKey: "teamsDesc",
  },
] as const;

type Category = (typeof CATEGORIES)[number]["value"];

const MAX_SIZE_MB = 5;
const ACCEPTED_TYPES = ["image/jpeg", "image/png", "image/webp"];

interface GbpAddPhotosDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function GbpAddPhotosDialog({ open, onOpenChange }: GbpAddPhotosDialogProps) {
  const { t } = useTranslation("reviews");
  const { currentSalon } = useSalonStore();
  const inputRef = useRef<HTMLInputElement>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [category, setCategory] = useState<Category>("ADDITIONAL");
  const [fileError, setFileError] = useState<string | null>(null);

  const apolloClient = useApolloClient();
  const [upload] = useMutation(UPLOAD_GBP_PHOTO_MUTATION);
  const [loading, setLoading] = useState(false);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const picked = e.target.files?.[0];
    if (!picked) return;
    setFileError(null);

    if (!ACCEPTED_TYPES.includes(picked.type)) {
      setFileError(t("gbpAddPhotos.errorUnsupportedType"));
      return;
    }
    if (picked.size > MAX_SIZE_MB * 1024 * 1024) {
      setFileError(t("gbpAddPhotos.errorFileTooLarge", { maxMb: MAX_SIZE_MB }));
      return;
    }

    setFile(picked);
    const reader = new FileReader();
    reader.onload = (ev) => setPreview(ev.target?.result as string);
    reader.readAsDataURL(picked);
  };

  const handleSubmit = async () => {
    if (!file || !currentSalon) return;
    setLoading(true);
    try {
      const { storageKey } = await uploadAsset(apolloClient, {
        file,
        kind: "GBP_PHOTO_TEMP",
        salonId: currentSalon.id,
      });
      await upload({
        variables: {
          salonId: currentSalon.id,
          input: { storageKey, category },
        },
      });
      toast.success(t("gbpAddPhotos.toastSuccess"));
      handleClose();
    } catch {
      toast.error(t("gbpAddPhotos.toastError"));
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setPreview(null);
    setFile(null);
    setFileError(null);
    setCategory("ADDITIONAL");
    if (inputRef.current) inputRef.current.value = "";
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ImagePlus className="w-5 h-5" />
            {t("gbpAddPhotos.dialogTitle")}
          </DialogTitle>
          <DialogDescription>{t("gbpAddPhotos.dialogDescription")}</DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-4 py-1">
          {/* Photo picker */}
          {/* biome-ignore lint/a11y/useSemanticElements: Needs to be a div because it contains a nested button */}
          <div
            className="border-2 border-dashed rounded-lg p-6 text-center cursor-pointer hover:border-foreground/30 transition-colors"
            onClick={() => inputRef.current?.click()}
            onKeyDown={(e) => e.key === "Enter" && inputRef.current?.click()}
            role="button"
            tabIndex={0}
          >
            {preview ? (
              <div className="relative inline-block">
                <img
                  src={preview}
                  alt="Preview"
                  className="max-h-48 max-w-full rounded-md object-contain"
                />
                <button
                  type="button"
                  className="absolute -top-2 -right-2 rounded-full bg-destructive text-destructive-foreground p-0.5"
                  onClick={(e) => {
                    e.stopPropagation();
                    setPreview(null);
                    setFile(null);
                    if (inputRef.current) inputRef.current.value = "";
                  }}
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            ) : (
              <div className="space-y-1">
                <ImagePlus className="w-8 h-8 mx-auto text-muted-foreground" />
                <p className="text-sm text-muted-foreground">{t("gbpAddPhotos.clickToSelect")}</p>
                <p className="text-xs text-muted-foreground">{t("gbpAddPhotos.fileHint")}</p>
              </div>
            )}
          </div>
          <input
            ref={inputRef}
            type="file"
            accept={ACCEPTED_TYPES.join(",")}
            className="hidden"
            onChange={handleFileChange}
          />
          {fileError && <p className="text-xs text-destructive">{fileError}</p>}

          {/* Category */}
          <div className="space-y-1.5">
            <Label>{t("gbpAddPhotos.photoTypeLabel")}</Label>
            <div className="grid grid-cols-1 gap-1.5">
              {CATEGORIES.map((c) => (
                <button
                  key={c.value}
                  type="button"
                  onClick={() => setCategory(c.value as Category)}
                  className={cn(
                    "text-left rounded-lg border px-3 py-2.5 transition-colors",
                    category === c.value
                      ? "border-primary bg-primary/5 ring-1 ring-primary/30"
                      : "border-border hover:border-foreground/30 hover:bg-muted/40"
                  )}
                >
                  <p className="text-sm font-medium leading-none">
                    {t(`gbpAddPhotos.categories.${c.labelKey}`)}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {t(`gbpAddPhotos.categories.${c.descKey}`)}
                  </p>
                </button>
              ))}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={loading}>
            {t("gbpAddPhotos.cancel")}
          </Button>
          <Button onClick={handleSubmit} disabled={!file || loading}>
            {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {t("gbpAddPhotos.uploadPhoto")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
